import React, { useState, useEffect } from "react";

const TipCalculator = () => {
  const [billAmount, setBillAmount] = useState(1000);
  const [tipPercentage, setTipPercentage] = useState(15);
  const [numberOfPeople, setNumberOfPeople] = useState(1);
  const [serviceQuality, setServiceQuality] = useState("good");
  const [roundUp, setRoundUp] = useState(false);
  const [financialYear, setFinancialYear] = useState("2023-24");
  const [result, setResult] = useState({
    billAmount: "0.00",
    tipPercentage: "0.00",
    tipAmount: "0.00",
    totalAmount: "0.00",
    perPersonBill: "0.00",
    perPersonTip: "0.00",
    perPersonTotal: "0.00",
    serviceQuality: "",
    savingTip: "",
  });

  // Tip Calculation Function
  const calculateTip = () => {
    let bill = parseFloat(billAmount);
    let tip = parseFloat(tipPercentage);
    let people = parseFloat(numberOfPeople);

    if (isNaN(bill) || bill < 0 || isNaN(tip) || tip < 0 || isNaN(people) || people < 1) return;

    // Calculate tip amount
    let tipAmount = (bill * tip) / 100;
    
    // Round up if selected
    if (roundUp) {
      tipAmount = Math.ceil(tipAmount);
    }
    
    // Calculate total amount
    const totalAmount = bill + tipAmount;
    
    // Calculate per person amounts
    const perPersonBill = bill / people;
    const perPersonTip = tipAmount / people;
    const perPersonTotal = totalAmount / people;

    let qualityText = "";
    let savingTip = "";

    switch(serviceQuality) {
      case "excellent":
        qualityText = "Excellent Service (20-25%)";
        savingTip = "Excellent service deserves excellent tips! Your server went above and beyond.";
        break;
      case "good":
        qualityText = "Good Service (15-20%)";
        savingTip = "Standard tipping for good service. This is the most common tip percentage.";
        break;
      case "average":
        qualityText = "Average Service (10-15%)";
        savingTip = "Fair tip for average service. Consider the effort your server put in.";
        break;
      case "poor":
        qualityText = "Poor Service (5-10%)";
        savingTip = "Lower tip for below-average service. You may want to speak with management.";
        break;
      case "custom":
        qualityText = `Custom Tip (${tip}%)`;
        savingTip = "Custom tip percentage set. Adjust based on your experience.";
        break;
      default:
        qualityText = "Standard Tip";
        savingTip = "Adjust tip based on service quality and local customs.";
    }

    setResult({
      billAmount: bill.toFixed(2),
      tipPercentage: tip.toFixed(2),
      tipAmount: tipAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      perPersonBill: perPersonBill.toFixed(2),
      perPersonTip: perPersonTip.toFixed(2),
      perPersonTotal: perPersonTotal.toFixed(2),
      serviceQuality: qualityText,
      savingTip,
    });
  };

  useEffect(() => {
    calculateTip();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>Tip Calculator</h1>
          <p>
            Calculate tip amount and split bills easily among friends for restaurants, delivery, and services
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Bill Details
              </h2>

              {/* Bill Amount */}
              <div className="input-group">
                <label>Bill Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={billAmount}
                    onChange={(e) => setBillAmount(e.target.value)}
                    className="calc-input"
                    placeholder="1000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500, 1000, 2000, 5000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setBillAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Service Quality */}
              <div className="input-group">
                <label>Service Quality</label>
                <div className="quick-select-group">
                  {[
                    { value: "excellent", label: "Excellent (20%)" },
                    { value: "good", label: "Good (15%)" },
                    { value: "average", label: "Average (12%)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setServiceQuality(type.value);
                        if (type.value === "excellent") setTipPercentage(20);
                        else if (type.value === "good") setTipPercentage(15);
                        else if (type.value === "average") setTipPercentage(12);
                      }}
                      className={`quick-select-btn ${serviceQuality === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "poor", label: "Poor (8%)" },
                    { value: "custom", label: "Custom %" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setServiceQuality(type.value);
                        if (type.value === "poor") setTipPercentage(8);
                      }}
                      className={`quick-select-btn ${serviceQuality === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tip Percentage */}
              <div className="input-group">
                <label>Tip Percentage (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={tipPercentage}
                    onChange={(e) => setTipPercentage(e.target.value)}
                    className="calc-input"
                    placeholder="15"
                    step="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[10, 15, 18, 20].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setTipPercentage(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Number of People */}
              <div className="input-group">
                <label>Number of People</label>
                <div className="input-wrapper">
                  <span className="input-prefix">#</span>
                  <input
                    type="number"
                    value={numberOfPeople}
                    onChange={(e) => setNumberOfPeople(e.target.value)}
                    className="calc-input"
                    placeholder="1"
                    min="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 2, 4, 6].map((people) => (
                    <button
                      key={people}
                      onClick={() => setNumberOfPeople(people)}
                      className="quick-select-btn success"
                    >
                      {people} {people === 1 ? 'Person' : 'People'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Round Up */}
              <div className="input-group">
                <label>Round Up Tip?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Round Up)" },
                    { value: false, label: "No (Exact)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setRoundUp(type.value)}
                      className={`quick-select-btn ${roundUp === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Financial Year */}
              <div className="input-group">
                <label>Currency Format</label>
                <div className="quick-select-group">
                  {[
                    { value: "2023-24", label: "Indian (₹)" },
                    { value: "2024-25", label: "US Dollar ($)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setFinancialYear(type.value)}
                      className={`quick-select-btn ${financialYear === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateTip} className="calculate-btn">
                Calculate Tip
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Tip Calculation
              </h2>

              <div className="result-card">
                {/* Total Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Amount (Bill + Tip)</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.serviceQuality} at {result.tipPercentage}%
                  </div>
                </div>

                {/* Per Person Total */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Per Person Total</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.perPersonTotal).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Each person pays (including tip)
                  </div>
                </div>

                {/* Tip Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Tip Amount</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.tipAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {result.tipPercentage}% tip rate
                  </div>
                </div>

                {/* Bill Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Original Bill Amount</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.billAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Before tip
                  </div>
                </div>

                {/* Per Person Tip */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Per Person Tip</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.perPersonTip).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Tip share per person
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Amount Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Bill Amount</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.billAmount).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseFloat(result.totalAmount) > 0 ? (parseFloat(result.billAmount) / parseFloat(result.totalAmount)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Tip ({result.tipPercentage}%)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.tipAmount).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseFloat(result.totalAmount) > 0 ? (parseFloat(result.tipAmount) / parseFloat(result.totalAmount)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Per Person Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Per Person Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Bill Share Per Person</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.perPersonBill) / parseFloat(result.perPersonTotal)) * 100).toFixed(2)}% (₹{Number(result.perPersonBill).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.perPersonBill) / parseFloat(result.perPersonTotal)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Tip Share Per Person</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.perPersonTip) / parseFloat(result.perPersonTotal)) * 100).toFixed(2)}% (₹{Number(result.perPersonTip).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.perPersonTip) / parseFloat(result.perPersonTotal)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Tipping Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Tipping Etiquette</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.savingTip} Tipping customs vary by country and service type.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Tipping</h3>
              <p>
                Tipping is a customary practice of giving extra money to service workers for their service. In restaurants, 
                the standard tip ranges from 15-20% of the pre-tax bill amount in most countries. Excellent service typically 
                warrants 20-25%, while average service may receive 10-15%. In India, tipping 10% is common in restaurants, 
                though it's becoming more generous in metro cities. Tips are not mandatory but are an important part of service 
                workers' income in many countries, especially where minimum wages are low. For delivery services, ₹20-50 is 
                typical depending on distance and order size. Hotel staff, taxi drivers, and salon workers also commonly 
                receive tips. When splitting bills among friends, ensure everyone contributes equally to both the bill and tip. 
                Some establishments automatically add a service charge (usually 10%) to bills - check before adding an 
                additional tip. Cash tips are often preferred as they go directly to the server.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Tip Calculation Formula:</h4>
            <div className="formula-display">
              Total Amount = Bill Amount + (Bill Amount × Tip Percentage / 100)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Tip Amount</div>
                <div className="formula-param-value">Bill Amount × Tip Percentage / 100</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Per Person Total</div>
                <div className="formula-param-value">Total Amount / Number of People</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Standard Tip</div>
                <div className="formula-param-value">15-20% for good service</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Excellent Tip</div>
                <div className="formula-param-value">20-25% for exceptional service</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the standard tip percentage?</h5>
              <p>
                In most countries, 15-20% is standard for restaurants. In India, 10% is common and acceptable. Adjust based on service quality and local customs.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I tip on the pre-tax or post-tax amount?</h5>
              <p>
                Traditionally, tips are calculated on the pre-tax bill amount. However, tipping on the total (including tax) is also acceptable and increasingly common.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if service charge is already included?</h5>
              <p>
                Check your bill carefully. If a service charge (usually 10%) is included, an additional tip is optional but appreciated for exceptional service.
              </p>
            </div>

            <div className="faq-item">
              <h5>How much should I tip for delivery?</h5>
              <p>
                For food delivery in India, ₹20-50 is typical based on distance and order size. In bad weather or long distances, consider tipping more generously.
              </p>
            </div>

            <div className="faq-item">
              <h5>Is tipping mandatory?</h5>
              <p>
                Tipping is not legally mandatory in most places, but it's a social norm and expected in service industries. Service workers often rely on tips as part of their income.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I tip in cash or add to card payment?</h5>
              <p>
                Cash tips are preferred as they go directly to the server. If paying by card, ask the server if they receive card tips or if cash is better.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TipCalculator;